import { useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import { emptyListSentence } from '../../lib/portalAssets/pageState';
import {
  accountAssetsParams,
  accountRangeIsBackwards,
  EMPTY_ACCOUNT_ASSETS_FORM,
  hasAnyAccountFilter,
  type AccountAssetsForm,
} from '../../lib/portalAssets/accountAssetsView';
import { narrowMode } from '../../lib/portalAssets/sortSearchView';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { AssetFilterBar } from './AssetFilterBar';
import { AssetGrid } from './AssetGrid';
import { AssetUploader } from './AssetUploader';
import { BulkActionBar } from './BulkActionBar';
import { StaffAssetDialog } from './StaffAssetDialog';
import { staffAssetsApi, staffBulkApi } from './staffApi';
import { useBulkSelection } from './useBulkSelection';
import { useImageErrorRefetch } from './useImageErrorRefetch';

// THE ACCOUNT PAGE'S "Assets" TAB (spec §8, commit 13) — the staff side of the
// loop: a club uploads, Kyck reviews here, approved assets come back to them.
//
// ⚠️ STAFF ENDPOINTS ONLY (staffApi.ts). The tile names the real reviewer here —
// a colleague, or the club person who decided (2026-10-08: clubs review too) —
// while the club's own tile says "Kyck" for any staff member (Q4). The same
// data, two audiences, decided server-side in tile.ts.
//
// ⚠️ Opens the asset in a DIALOG, unlike the portal (which uses a page): the
// staff app's tokens are the ones a portaled dialog takes, so here it is right.

export function AccountAssetsSection({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<AccountAssetsForm>(EMPTY_ACCOUNT_ASSETS_FORM);
  // ⚠️ The box disables the other filters at once (raw); the QUERY follows the debounced text. The disabled values stay in
  // the form, so clearing the search restores the page as it was (see sortSearchView.ts).
  const debouncedSearch = useDebouncedValue(form.search, 300);
  const backwards = narrowMode(form) === null && accountRangeIsBackwards(form);
  const filtered = hasAnyAccountFilter(form);
  const params = accountAssetsParams({ ...form, search: debouncedSearch }, accountId);
  const [openId, setOpenId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  // ⚠️ Recently deleted is its own VIEW, not a status: it takes no filters
  // (the server refuses them), and it is where Restore lives.
  const [showDeleted, setShowDeleted] = useState(false);

  const list = useInfiniteQuery({
    queryKey: ['portal-assets-staff', 'list', accountId, showDeleted ? 'deleted' : params],
    queryFn: ({ pageParam }) =>
      staffAssetsApi.list(showDeleted ? { accountId, view: 'deleted', cursor: pageParam } : { ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: showDeleted || !backwards,
    retry: false,
  });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['portal-assets-staff'] });
  const onImageError = useImageErrorRefetch(list.refetch);

  const tiles = list.data?.pages.flatMap((p) => p.tiles) ?? [];
  // STAFF BULK (2026-10-13). Not in Recently deleted: there is nothing there to bulk-act on.
  const bulk = useBulkSelection(showDeleted ? [] : tiles);
  const categories = useQuery({
    queryKey: ['portal-assets-staff', 'categories-for-account', accountId],
    queryFn: () => staffAssetsApi.categories(accountId),
    retry: false,
  });

  // Not filters, so apart from them. "Upload for this club" is the page's primary action (the green one).
  const actions = (
    <>
      <Button size="sm" variant="outline" onClick={() => setShowDeleted((d) => !d)}>
        {showDeleted ? 'Back to live assets' : 'Recently deleted'}
      </Button>
      <Button size="sm" onClick={() => setUploading((u) => !u)}>
        {uploading ? 'Close upload' : 'Upload for this club'}
      </Button>
    </>
  );

  return (
    <div className="space-y-4">
      <p className="max-w-prose text-sm text-muted-foreground">
        Photos and videos this club has uploaded through the client portal. Kyck and the club can both approve or
        not approve, and either can change the other's decision — each asset's history shows who decided what. A
        club sees any Kyck reviewer as “Kyck”.
      </p>
      {showDeleted ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">{actions}</div>
      ) : (
        <AssetFilterBar
          form={form}
          setForm={setForm}
          categories={(categories.data?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
          showKyckFilter
          filtered={filtered}
          onClear={() => setForm((f) => ({ ...EMPTY_ACCOUNT_ASSETS_FORM, sort: f.sort }))}
          actions={actions}
        />
      )}

      {uploading && <AssetUploader audience="staff" api={staffAssetsApi} accountId={accountId} onFinished={invalidate} />}

      {backwards && !showDeleted ? (
        <p className="text-sm text-destructive" role="alert">
          The start date is after the end date.
        </p>
      ) : list.isPending ? (
        <CenteredSpinner />
      ) : list.isError ? (
        <p className="text-sm text-muted-foreground">
          {list.error instanceof ApiError && list.error.status === 400
            ? list.error.message
            : "Couldn't load this club's assets."}
        </p>
      ) : tiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {showDeleted ? 'Nothing has been deleted for this club.' : emptyListSentence(filtered)}
        </p>
      ) : (
        <>
          {!showDeleted && (
            <BulkActionBar
              tiles={bulk.selectedTiles}
              categories={categories.data ? categories.data.categories : null}
              categoryNote="This club's categories couldn't be loaded, so category actions are unavailable."
              onClear={bulk.clear}
              onKeepOnly={bulk.keepOnly}
              onDone={invalidate}
              selectNote={bulk.note}
              onSelectAll={bulk.selectAllVisible}
              api={staffBulkApi}
              audience="staff"
            />
          )}
          <AssetGrid
            fill
            tiles={tiles}
            onOpen={(t) => setOpenId(t.id)}
            onImageError={onImageError}
            selection={showDeleted ? undefined : { isSelected: bulk.isSelected, onToggle: bulk.toggle }}
          />
        </>
      )}
      {list.hasNextPage && (
        <Button size="sm" variant="outline" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
          {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}

      <StaffAssetDialog assetId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
